import { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function MyProfileSettings({ onProfileUpdate }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [socialLinks, setSocialLinks] = useState([{ platform: "", url: "" }]);
  const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;
  const API_BASE = `${TEST_BASE_URL}`;
  const API_URL = `${API_BASE}/user-profile`;

  const getFullAvatarUrl = (avatar) => {
    if (!avatar) return null;
    if (avatar.startsWith("http")) return avatar;
    if (avatar.startsWith("data:")) return avatar;
    const cleanedPath = avatar.replace(/^\/+/, "");
    return `https://rccgbournemouth.org/storage/${cleanedPath}`;
  };

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("authToken");

        if (!token) {
          throw new Error("No authentication token found. Please log in.");
        }

        console.log("📡 Fetching profile...");

        const res = await fetch(API_URL, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        console.log("📥 Profile fetch status:", res.status);

        if (!res.ok) {
          const text = await res.text();
          console.error(" Server error:", text);
          throw new Error(`Failed to fetch profile (${res.status})`);
        }

        const data = await res.json();
        console.log(" Profile data:", data);

        if (data.status === "success" && data.data) {
          const profileData = {
            ...data.data,
            avatar: getFullAvatarUrl(data.data.avatar),
          };
          setProfile(profileData);

          if (profileData.avatar) {
            localStorage.setItem("userAvatar", profileData.avatar);
          }
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        console.error("Profile fetch error:", err);
        setError(err.message);
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [API_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!profile) return;

    if (!profile.name?.trim()) {
      toast.error("Name is required");
      return;
    }

    if (
      profile.phone_number?.trim() &&
      !/^\+?[\d\s\-()]+$/.test(profile.phone_number.trim())
    ) {
      toast.error("Invalid phone number format");
      return;
    }

    for (const link of socialLinks) {
      if (link.url?.trim() && !/^https?:\/\/\S+$/.test(link.url.trim())) {
        toast.error("Please enter valid URLs for your social media links");
        return;
      }
    }

    const updateData = {
      name: String(profile.name || "").trim(),
    };

    const phoneNumber = String(profile.phone_number || "").trim();
    if (phoneNumber) {
      updateData.phone_number = phoneNumber;
    }

    const location = String(profile.location || "").trim();
    if (location) {
      updateData.location = location;
    }

    const profession = String(profile.profession || "").trim();
    if (profession) {
      updateData.profession = profession;
    }

    const bio = String(profile.bio || "").trim();
    if (bio) {
      updateData.bio = bio;
    }

    const shortTestimony = String(profile.short_testimony || "").trim();
    if (shortTestimony) {
      updateData.short_testimony = shortTestimony;
    }

    socialLinks.forEach((link) => {
      if (link.platform && link.url?.trim()) {
        updateData[`${link.platform}_url`] = String(link.url).trim();
      }
    });

    setSaving(true);

    try {
      const token = localStorage.getItem("authToken");

      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }

      console.log("📤 Sending profile update:", updateData);

      const response = await fetch(API_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: JSON.stringify(updateData),
      });

      console.log("📥 Response status:", response.status);

      const responseText = await response.text();
      console.log("📄 Raw response:", responseText);

      if (!response.ok) {
        let errorMessage = `Failed to update profile (${response.status})`;

        try {
          const errorData = JSON.parse(responseText);
          console.error(" Error data:", errorData);

          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.errors) {
            const validationErrors = Object.values(errorData.errors).flat();
            errorMessage = validationErrors.join(", ");
          }
        } catch (parseError) {
          console.error(" Could not parse error response");
        }

        throw new Error(errorMessage);
      }

      let data;
      try {
        data = JSON.parse(responseText);
        console.log(" Update successful:", data);
      } catch (parseError) {
        throw new Error("Invalid response format from server");
      }

      if (data.status === "success" && data.data) {
        const updatedProfile = {
          ...data.data,
          avatar: getFullAvatarUrl(data.data.avatar),
        };

        setProfile(updatedProfile);
        toast.success("Profile updated successfully!");

        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        const updatedUser = {
          ...storedUser,
          name: updatedProfile.name,
          email: updatedProfile.email,
          phone_number: updatedProfile.phone_number,
          location: updatedProfile.location,
          profession: updatedProfile.profession,
          bio: updatedProfile.bio,
          short_testimony: updatedProfile.short_testimony,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));

        if (updatedProfile.avatar) {
          localStorage.setItem("userAvatar", updatedProfile.avatar);
        }

        if (onProfileUpdate) {
          onProfileUpdate(updatedProfile);
        }

        window.dispatchEvent(
          new CustomEvent("userProfileUpdated", {
            detail: {
              name: updatedProfile.name,
              avatar: updatedProfile.avatar,
            },
          })
        );
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error(" Profile update failed:", err);
      toast.error(`Update failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSocialChange = (index, field, value) => {
    const updated = [...socialLinks];
    updated[index][field] = value;
    setSocialLinks(updated);
  };

  const addSocialLink = () => {
    setSocialLinks([...socialLinks, { platform: "", url: "" }]);
  };

  const removeSocialLink = (index) => {
    const updated = socialLinks.filter((_, i) => i !== index);
    setSocialLinks(updated);
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.match(/^image\/(png|jpeg|jpg)$/)) {
      toast.error("Only PNG and JPG images are allowed");
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      toast.error("File must be less than 3MB");
      return;
    }

    setUploading(true);

    try {
      const token = localStorage.getItem("authToken");
      if (!token)
        throw new Error("No authentication token found. Please log in.");

      console.log("📤 Uploading avatar...");
      console.log("📦 File details:", {
        name: file.name,
        type: file.type,
        size: `${(file.size / 1024).toFixed(2)} KB`,
      });

      const formData = new FormData();
      formData.append("avatar", file);

      console.log("📋 FormData contents:");
      for (let [key, value] of formData.entries()) {
        console.log(
          `  ${key}:`,
          value instanceof File ? `File(${value.name})` : value
        );
      }

      console.log(" Uploading to:", API_URL);
      console.log("🔑 Using token:", token.substring(0, 20) + "...");
      const response = await fetch(API_URL, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      });

      console.log("📥 Upload response status:", response.status);
      console.log("📥 Response headers:", {
        contentType: response.headers.get("content-type"),
        contentLength: response.headers.get("content-length"),
      });

      const responseText = await response.text();
      console.log("📄 Raw upload response:", responseText);

      if (!response.ok) {
        let errorMsg = `Upload failed (${response.status})`;
        try {
          const errorData = JSON.parse(responseText);
          console.error(" Error data:", errorData);
          errorMsg = errorData.message || errorData.error || errorMsg;
        } catch (err) {
          console.error(" Could not parse upload error response");
          errorMsg = responseText || errorMsg;
        }
        throw new Error(errorMsg);
      }

      const data = JSON.parse(responseText);
      console.log(" Upload response parsed:", data);

      if (data.status === "success" && data.data?.avatar) {
        const avatarPath = data.data.avatar;
        console.log("🖼️ Avatar path from server:", avatarPath);
        if (avatarPath.includes("default-avatar")) {
          console.warn(
            "⚠️ WARNING: Backend returned default avatar - file may not have been uploaded"
          );
          throw new Error(
            "File upload failed - backend returned default avatar. Check backend logs."
          );
        }

        const fullAvatarUrl = getFullAvatarUrl(avatarPath);
        const timestamp = new Date().getTime();
        const avatarUrlWithCacheBust = `${fullAvatarUrl}?t=${timestamp}`;

        console.log("🖼️ Full URL:", fullAvatarUrl);
        console.log("🖼️ URL with cache bust:", avatarUrlWithCacheBust);

        const updatedProfile = { ...profile, avatar: avatarUrlWithCacheBust };
        setProfile(updatedProfile);
        localStorage.setItem("userAvatar", avatarUrlWithCacheBust);

        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem(
          "user",
          JSON.stringify({ ...storedUser, avatar: avatarUrlWithCacheBust })
        );

        toast.success("Profile picture updated successfully!");

        if (onProfileUpdate) onProfileUpdate(updatedProfile);

        window.dispatchEvent(
          new CustomEvent("userProfileUpdated", {
            detail: {
              name: updatedProfile.name,
              avatar: avatarUrlWithCacheBust,
            },
          })
        );

        setTimeout(() => {
          const imgs = document.querySelectorAll(
            "img[alt*='Profile'], .profile-avatar, .avatarprofile"
          );
          console.log(" Reloading", imgs.length, "avatar images");
          imgs.forEach((img) => {
            const currentSrc = img.src.split("?")[0];
            img.src = currentSrc + "?t=" + timestamp;
          });
        }, 100);
      } else {
        throw new Error("Invalid response format from server");
      }
    } catch (err) {
      console.error(" Avatar upload failed:", err);
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!profile.avatar) return;

    if (
      !window.confirm("Are you sure you want to remove your profile picture?")
    ) {
      return;
    }

    setUploading(true);

    try {
      const token = localStorage.getItem("authToken");
      if (!token)
        throw new Error("No authentication token found. Please log in.");

      console.log("🗑️ Deleting avatar...");

      const response = await fetch(`${API_URL}/avatar`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      console.log("📥 Delete response status:", response.status);

      if (!response.ok) {
        const responseText = await response.text();
        let errorMsg = `Failed to delete (${response.status})`;
        try {
          const errorData = JSON.parse(responseText);
          errorMsg = errorData.message || errorData.error || errorMsg;
        } catch (err) {
          console.error(" Could not parse delete error response");
        }
        throw new Error(errorMsg);
      }

      const data = await response.json();
      console.log(" Avatar deleted:", data);

      const updatedProfile = { ...profile, avatar: null };
      setProfile(updatedProfile);
      localStorage.removeItem("userAvatar");

      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem(
        "user",
        JSON.stringify({ ...storedUser, avatar: null })
      );

      toast.success("Profile picture removed successfully!");

      if (onProfileUpdate) onProfileUpdate(updatedProfile);

      window.dispatchEvent(
        new CustomEvent("userProfileUpdated", {
          detail: { name: updatedProfile.name, avatar: null },
        })
      );
    } catch (err) {
      console.error(" Avatar deletion failed:", err);
      toast.error(`Failed to remove avatar: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="settings-content text-center p-5">
        <p>Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="settings-content text-center p-5">
        <p className="text-danger">{error}</p>
        <button
          className="btn btn-primary mt-3"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="settings-content text-center p-5">
        <p>No profile data available</p>
      </div>
    );
  }

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ");
    const initials = parts.map((p) => p[0]?.toUpperCase() || "").slice(0, 2);
    return initials.join("");
  };

  return (
    <>
      <div className="settings-content">
        <h5>My Profile Settings</h5>

        <div className="profile-header">
          <div
            className="profile-avatar-wrapper position-relative"
            style={{ position: "relative" }}
          >
            {profile.avatar ? (
              <img
                key={profile.avatar}
                src={profile.avatar}
                alt={profile.name || "Profile"}
                className="profile-avatar"
                onError={(e) => {
                  console.warn(" Image load failed, showing initials");
                  e.target.style.display = "none";
                  e.target.parentNode
                    .querySelector(".avatar-initials")
                    ?.classList.remove("d-none");
                }}
              />
            ) : null}

            <div
              className={`avatar-initials d-flex align-items-center justify-content-center ${
                profile.avatar ? "d-none" : ""
              }`}
            >
              {getInitials(profile.name)}
            </div>

            {uploading && (
              <div className="avatar-overlay">
                <span>Uploading...</span>
              </div>
            )}
          </div>

          <div>
            <div className="profile-header-buttons mt-2">
              <label className="upload-btn">
                {uploading ? "Uploading..." : "Upload"}
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  onChange={handleAvatarChange}
                  disabled={uploading}
                  style={{ display: "none" }}
                />
              </label>
              <button
                type="button"
                disabled={uploading || !profile.avatar}
                onClick={handleRemoveAvatar}
              >
                Remove
              </button>
            </div>
            <span>Max size 3MB • PNG or JPG formats only.</span>
          </div>
        </div>

        <div className="mt-3">
          <label>
            Full Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            value={profile.name || ""}
            onChange={(e) => setProfile({ ...profile, name: e.target.value })}
            className="form-control"
            required
            placeholder="Enter your full name"
          />

          <div className="row">
            <div className="col-md-6">
              <label>
                Email <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                value={profile.email || ""}
                className="form-control"
                disabled
                style={{ backgroundColor: "#f0f0f0", cursor: "not-allowed" }}
              />
            </div>
            <div className="col-md-6">
              <label>Phone Number</label>
              <input
                type="tel"
                value={profile.phone_number || ""}
                onChange={(e) =>
                  setProfile({ ...profile, phone_number: e.target.value })
                }
                className="form-control"
                placeholder="+xxx xxx xxx xxx"
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <label>Location</label>
              <input
                type="text"
                value={profile.location || ""}
                onChange={(e) =>
                  setProfile({ ...profile, location: e.target.value })
                }
                className="form-control"
                placeholder="City, Country"
              />
            </div>
            <div className="col-md-6">
              <label>Profession</label>
              <input
                type="text"
                value={profile.profession || ""}
                onChange={(e) =>
                  setProfile({ ...profile, profession: e.target.value })
                }
                className="form-control"
                placeholder="Your profession"
              />
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-6">
              <label>Bio</label>
              <textarea
                rows="7"
                value={profile.bio || ""}
                onChange={(e) =>
                  setProfile({ ...profile, bio: e.target.value })
                }
                className="form-control"
                placeholder="Tell us about yourself..."
                maxLength="500"
              />
              <small className="text-muted">
                {(profile.bio || "").length}/500 characters
              </small>
            </div>
            <div className="col-md-6">
              <label>Short Testimony</label>
              <textarea
                rows="7"
                value={profile.short_testimony || ""}
                onChange={(e) =>
                  setProfile({ ...profile, short_testimony: e.target.value })
                }
                className="form-control"
                placeholder="Share your testimony..."
                maxLength="500"
              />
              <small className="text-muted">
                {(profile.short_testimony || "").length}/500 characters
              </small>
            </div>
          </div>

          <div className="row mt-5">
            <small className="text-muted fs-6 mb-3">Social Media Links</small>

            {socialLinks.map((link, index) => (
              <div className="row mb-3" key={index}>
                <div className="col-md-5">
                  <label>Social Media</label>
                  <select
                    className="form-control"
                    value={link.platform}
                    onChange={(e) =>
                      handleSocialChange(index, "platform", e.target.value)
                    }
                  >
                    <option value="">Choose Social Media</option>
                    <option value="facebook">Facebook</option>
                    <option value="x">X (Twitter)</option>
                    <option value="instagram">Instagram</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="whatsapp">WhatsApp</option>
                  </select>
                </div>

                <div className="col-md-5">
                  <label>Link</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter link"
                    value={link.url}
                    onChange={(e) =>
                      handleSocialChange(index, "url", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-2 d-flex align-items-end">
                  {index > 0 && (
                    <button
                      type="button"
                      className="btn btn-outline-danger w-100"
                      onClick={() => removeSocialLink(index)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}

            <div
              className="add-social-media mt-3 d-flex align-items-center text-primary"
              style={{ cursor: "pointer" }}
              onClick={addSocialLink}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
              >
                <path
                  d="M10.75 6.75V14.75M14.75 10.75L6.75 10.75"
                  stroke="#0045E8"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20.75 10.75C20.75 5.22715 16.2728 0.75 10.75 0.75C5.22715 0.75 0.75 5.22715 0.75 10.75C0.75 16.2728 5.22715 20.75 10.75 20.75C16.2728 20.75 20.75 16.2728 20.75 10.75Z"
                  stroke="#0045E8"
                  strokeWidth="1.5"
                />
              </svg>
              <span className="ms-2 text-primary">
                Add Another Social Media
              </span>
            </div>
          </div>

          <div className="profile-buttons">
            <button type="button" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving}
              onClick={handleSubmit}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={1500} />
    </>
  );
}

export default MyProfileSettings;
